import React, { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { X, Wallet, CheckCircle, Warning, ArrowSquareOut, Plus } from '@phosphor-icons/react';

const formatINR = (n) =>
  '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso.includes('T') ? iso : iso + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  } catch { return iso.slice(0, 10); }
};

/**
 * OpenBalancesModal — cross-event roll-up of every line with a positive
 * balance, so hosts can sweep dangling vendor payments in 60 seconds/week.
 */
export default function OpenBalancesModal({ onClose, onChanged }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(null);     // row key currently being paid
  const [payAmt, setPayAmt] = useState('');
  const [payDesc, setPayDesc] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/events/money/open-balances');
      setData(r.data);
    } catch (e) { setData({ rows: [], totals: { total_open: 0, rows_count: 0 } }); }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startPay = (row) => {
    setPaying(`${row.event_id}:${row.line_id}`);
    setPayAmt(String(Math.round(row.balance)));
    setPayDesc('');
  };

  const cancelPay = () => { setPaying(null); setPayAmt(''); setPayDesc(''); };

  const confirmPay = async (row) => {
    const amt = parseFloat(payAmt);
    if (!amt || amt <= 0) return;
    setSaving(true);
    try {
      await api.post('/cashbook/transactions', {
        type: 'debit',
        amount: amt,
        category: row.category,
        description: payDesc || `${row.description} payment`,
        payment_mode: 'UPI',
        date: new Date().toISOString().slice(0, 10),
        event_id: row.event_id,
        event_line_id: row.line_id,
      });
      cancelPay();
      await load();
      onChanged && onChanged();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to book payment');
    }
    setSaving(false);
  };

  const rows = data?.rows || [];
  const totals = data?.totals || {};

  // Group rows by event for cleaner reading
  const groups = {};
  for (const r of rows) {
    const key = r.event_id;
    if (!groups[key]) groups[key] = { event: r, items: [] };
    groups[key].items.push(r);
  }
  const grouped = Object.values(groups);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start sm:items-center justify-center p-3 sm:p-4 overflow-y-auto"
      onClick={onClose} data-testid="open-balances-modal">
      <div className="glass-card max-w-2xl w-full p-5 my-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <Wallet size={22} className="text-amber-400" />
            <div>
              <h3 className="font-heading text-base font-bold text-white">Open Balances</h3>
              <p className="text-[11px] text-neutral-500 mt-0.5">
                Pending vendor payments across all active events
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white" data-testid="open-balances-close">
            <X size={18} />
          </button>
        </div>

        {/* Totals strip */}
        {!loading && (
          <div className="grid grid-cols-3 gap-2 mb-4">
            <Stat label="Total Open"
              value={formatINR(totals.total_open || 0)}
              color="#F59E0B"
              testid="open-balances-total" />
            <Stat label="Confirmed"
              value={formatINR(totals.total_confirmed || 0)}
              color="#EF4444"
              sub="Final bill set" />
            <Stat label="Estimated"
              value={formatINR(totals.total_estimated || 0)}
              color="#A3A3A3"
              sub="Projected from budget" />
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10" data-testid="open-balances-empty">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/15 mb-3">
              <CheckCircle size={32} weight="fill" className="text-green-400" />
            </div>
            <p className="text-sm font-bold text-white">All caught up!</p>
            <p className="text-xs text-neutral-500 mt-1">No pending vendor payments across your active events.</p>
          </div>
        ) : (
          <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
            {grouped.map(({ event, items }) => (
              <div key={event.event_id} className="rounded-2xl border border-white/5 overflow-hidden">
                <div className="px-3 py-2 flex items-center gap-2"
                  style={{ background: `${event.event_color}15`, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <span className="text-lg">{event.event_emoji}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-white truncate" style={{ color: event.event_color }}>
                      {event.event_title}
                    </p>
                    {event.event_date && (
                      <p className="text-[10px] text-neutral-400">{fmtDate(event.event_date)}</p>
                    )}
                  </div>
                  <a href={`/events?open=${event.event_id}`} target="_blank" rel="noopener noreferrer"
                    title="Open event in new tab"
                    className="text-neutral-500 hover:text-white p-1.5">
                    <ArrowSquareOut size={13} />
                  </a>
                </div>
                {items.map((row) => {
                  const key = `${row.event_id}:${row.line_id}`;
                  const isPaying = paying === key;
                  const isEstimated = row.source === 'estimated';
                  return (
                    <div key={key} className="px-3 py-2.5 border-b border-white/5 last:border-0"
                      data-testid={`open-balance-row-${row.line_id}`}>
                      <div className="flex items-center gap-3 flex-wrap">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-semibold text-white truncate">{row.description}</p>
                            {isEstimated && (
                              <span title="No final bill yet — estimated from budget"
                                className="text-[8px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-neutral-700/50 text-neutral-300 flex items-center gap-0.5">
                                <Warning size={8} /> est
                              </span>
                            )}
                          </div>
                          <p className="text-[10px] text-neutral-500 uppercase tracking-wider mt-0.5">
                            {row.category}
                            <span className="ml-2 normal-case tracking-normal text-neutral-600">
                              · paid {formatINR(row.advance_paid)} / {formatINR(row.final_amount || row.planned)}
                            </span>
                          </p>
                        </div>
                        <div className="text-right">
                          <div className={`text-base font-extrabold ${isEstimated ? 'text-neutral-300' : 'text-amber-400'}`}>
                            {formatINR(row.balance)}
                          </div>
                          <div className="text-[9px] uppercase tracking-widest text-neutral-500">to pay</div>
                        </div>
                        {!isPaying && (
                          <button onClick={() => startPay(row)}
                            data-testid={`open-balance-pay-${row.line_id}`}
                            className="px-3 py-1.5 rounded-lg bg-green-500/15 text-green-400 border border-green-500/30 text-xs font-bold flex items-center gap-1 hover:bg-green-500/25">
                            <Plus size={11} weight="bold" /> Pay
                          </button>
                        )}
                      </div>

                      {/* Inline pay form */}
                      {isPaying && (
                        <div className="mt-2.5 pt-2.5 border-t border-white/5 flex items-center gap-2 flex-wrap"
                          data-testid={`open-balance-pay-form-${row.line_id}`}>
                          <span className="text-[10px] uppercase tracking-widest text-green-400 font-bold">Pay ₹</span>
                          <input type="number" autoFocus value={payAmt}
                            onChange={(e) => setPayAmt(e.target.value)}
                            className="w-24 px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white outline-none focus:border-green-500/50" />
                          <input type="text" value={payDesc}
                            onChange={(e) => setPayDesc(e.target.value)}
                            placeholder="Note (optional)"
                            className="flex-1 min-w-[120px] px-2 py-1.5 rounded-lg bg-white/5 border border-white/10 text-sm text-white outline-none focus:border-green-500/50" />
                          <button onClick={() => confirmPay(row)} disabled={saving || !payAmt}
                            data-testid={`open-balance-pay-confirm-${row.line_id}`}
                            className="px-3 py-1.5 rounded-lg bg-green-500 text-white text-xs font-bold disabled:opacity-40">
                            {saving ? '…' : 'Book'}
                          </button>
                          <button onClick={cancelPay}
                            className="px-2 py-1.5 rounded-lg text-neutral-500 hover:text-white text-xs">
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color, sub, testid }) {
  return (
    <div className="rounded-xl p-2.5"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
      <div className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">{label}</div>
      <div className="text-base font-extrabold mt-0.5" style={{ color }} data-testid={testid}>{value}</div>
      {sub && <div className="text-[9px] text-neutral-500 mt-0.5">{sub}</div>}
    </div>
  );
}
